import type { BulkMedicationUpdateFinishedDetail, ProgramAuditApplyFinishedDetail } from '@/stores/userInbox'

export const PROGRAM_AUDIT_APPLY_FINISHED_EVENT = 'vector:program-audit-apply-finished'
export const BULK_MEDICATION_UPDATE_FINISHED_EVENT = 'vector:bulk-medication-update-finished'

/** Program detail refetch after audit run completed or failed on the server (not needs-review; that flow is audit UI + inbox). */
export const PROGRAM_STALE_EVENT = 'vector:program-stale'

/** Advocate dashboard status chart refetch when hospital_funded totals change (e.g. funds received). */
export const HOMEPAGE_STATUS_CHART_STALE_EVENT = 'vector:homepage-status-chart-stale'

export type ResourceLifecycleAction = 'created' | 'updated' | 'deleted'

/** Server `resource.updated` (Reverb / Echo `.resource.updated`). */
export interface ResourceUpdatedPayload {
  v: number
  resource: 'program' | 'patient_case' | 'opportunity'

  /** Canonical resource UUID from server. */
  resource_uuid: string
  milestone: string
  origin: string

  /**
   * Optional server hints.
   * - program_audit: `phase` ("audit" | "apply"), `audit_status`
   * - bulk_medication: `status` (BulkMedicationUpdateStatus)
   */
  related?: Record<string, string>

  /** Omitted or `updated`: handled below. Other values reserved for future list/detail behavior. */
  action?: ResourceLifecycleAction
}

function bulkMedicationShouldNotifyFinished(bulkStatus: string, milestone: string): boolean {
  return (
    bulkStatus === 'completed'
    || bulkStatus === 'partial_success'
    || (bulkStatus === '' && milestone === 'automation_completed')
  )
}

function dispatchProgramAuditSideEffects(
  payload: ResourceUpdatedPayload,
  programUuid: string,
  related: Record<string, string>,
  milestone: string,
): void {
  const phase = related.phase ?? ''
  const auditStatus = related.audit_status ?? ''
  const auditUuid = related.audit_uuid
  const isAuditPhase = phase === 'audit' || phase === ''
  const isApplyPhase = phase === 'apply' || (phase === '' && milestone === 'automation_completed')

  if (
    isAuditPhase
    && (milestone === 'automation_succeeded' || milestone === 'automation_failed')
  ) {
    window.dispatchEvent(new CustomEvent<{ program_uuid: string }>(PROGRAM_STALE_EVENT, {
      detail: { program_uuid: programUuid },
    }))
  }

  const applyChangedData
    = milestone === 'automation_completed'
      || (milestone === 'automation_failed' && auditStatus === 'partial_failure')

  if (isApplyPhase && auditUuid && applyChangedData) {
    window.dispatchEvent(new CustomEvent<ProgramAuditApplyFinishedDetail>(PROGRAM_AUDIT_APPLY_FINISHED_EVENT, {
      detail: {
        program_uuid: programUuid,
        audit_uuid: auditUuid,
        automation_event_type: payload.milestone,
      },
    }))
  }
}

function dispatchBulkMedicationFinishedForProgram(
  payload: ResourceUpdatedPayload,
  programUuid: string,
  related: Record<string, string>,
): void {
  window.dispatchEvent(new CustomEvent<BulkMedicationUpdateFinishedDetail>(BULK_MEDICATION_UPDATE_FINISHED_EVENT, {
    detail: {
      program_uuid: programUuid,
      patient_case_uuid: undefined,
      bulk_medication_update_uuid: related.bulk_medication_update_uuid,
      bulk_patient_case_medication_update_uuid: undefined,
      automation_event_type: payload.milestone,
    },
  }))
}

function dispatchBulkMedicationFinishedForPatientCase(
  payload: ResourceUpdatedPayload,
  patientCaseUuid: string,
  related: Record<string, string>,
): void {
  window.dispatchEvent(new CustomEvent<BulkMedicationUpdateFinishedDetail>(BULK_MEDICATION_UPDATE_FINISHED_EVENT, {
    detail: {
      program_uuid: undefined,
      patient_case_uuid: patientCaseUuid,
      bulk_medication_update_uuid: undefined,
      bulk_patient_case_medication_update_uuid: related.bulk_patient_case_medication_update_uuid,
      automation_event_type: payload.milestone,
    },
  }))
}

function dispatchClaimFundingSideEffects(
  payload: ResourceUpdatedPayload,
  milestone: string,
  origin: string,
): boolean {
  if (
    origin !== 'claim_funding'
    || payload.resource !== 'opportunity'
    || milestone !== 'funds_received'
  )
    return false

  window.dispatchEvent(new CustomEvent(HOMEPAGE_STATUS_CHART_STALE_EVENT))

  return true
}

function dispatchBulkMedicationSideEffects(
  payload: ResourceUpdatedPayload,
  resourceUuid: string,
  related: Record<string, string>,
  milestone: string,
  origin: string,
): boolean {
  if (origin !== 'bulk_medication')
    return false

  const bulkStatus = related.status ?? ''
  if (!bulkMedicationShouldNotifyFinished(bulkStatus, milestone))
    return false

  if (payload.resource === 'program') {
    dispatchBulkMedicationFinishedForProgram(payload, resourceUuid, related)

    return true
  }

  if (payload.resource === 'patient_case') {
    dispatchBulkMedicationFinishedForPatientCase(payload, resourceUuid, related)

    return true
  }

  return false
}

/**
 * Maps `resource.updated` into the window events used for today’s refetch paths (program audit + bulk medication).
 * Extend here when new origins or lifecycle actions need UI work.
 */
export function dispatchResourceUpdatedSideEffects(payload: ResourceUpdatedPayload): void {
  if (typeof window === 'undefined' || payload.v !== 1)
    return

  const action = payload.action ?? 'updated'
  if (action !== 'updated')
    return

  const milestone = payload.milestone ?? ''
  const origin = payload.origin ?? ''
  const related = payload.related ?? {}
  const resourceUuid = payload.resource_uuid.trim()
  if (!resourceUuid)
    return

  if (origin === 'program_audit' && payload.resource === 'program') {
    dispatchProgramAuditSideEffects(payload, resourceUuid, related, milestone)

    return
  }

  if (dispatchClaimFundingSideEffects(payload, milestone, origin))
    return

  dispatchBulkMedicationSideEffects(payload, resourceUuid, related, milestone, origin)
}
